import React, { createContext, useContext, useCallback } from 'react';
import propTypes from 'prop-types';

export const availableLocales = ['en', 'sk'];
export const isLocaleSupported = locale => availableLocales.includes(locale);

const InternationalizationContext = createContext({
  locale: availableLocales[0],
  messages: {},
});

export const useLocale = () => {
  const { locale } = useContext(InternationalizationContext);
  return locale;
};

export const useTranslate = overriddenMessages => {
  const { locale: currentLocale, messages: currentMessages } = useContext(
    InternationalizationContext
  );

  const messages = overriddenMessages || currentMessages;

  const fn = useCallback(
    (id, overridenLocale) => {
      const hasMessage = Object.prototype.hasOwnProperty.call(messages, id);

      if (hasMessage) {
        const message = messages[id];
        const locale = overridenLocale || currentLocale;
        const hasTranslation = Object.prototype.hasOwnProperty.call(
          message,
          locale
        );
        if (hasTranslation) {
          const translatedMessage = message[locale];
          return translatedMessage;
        }
      }

      return undefined;
    },
    [currentLocale, messages]
  );

  return fn;
};

export const Message = ({ id, children: fn }) => (
  <InternationalizationContext.Consumer>
    {({ locale, messages }) => {
      const hasMessage = Object.prototype.hasOwnProperty.call(messages, id);
      if (hasMessage) {
        const message = messages[id];

        const hasTranslation = Object.prototype.hasOwnProperty.call(
          message,
          locale
        );
        if (hasTranslation) {
          const translatedMessage = message[locale];

          if (fn) {
            return fn(translatedMessage, message, locale);
          }

          return translatedMessage;
        }

        return (
          <Error>
            {`Message with id "${id}" was found but has no translation for locale "${locale}."`}
          </Error>
        );
      }

      return <Error>{`Message with id "${id}" was not found.`}</Error>;
    }}
  </InternationalizationContext.Consumer>
);

Message.propTypes = {
  id: propTypes.string,
  children: propTypes.func,
};

const Error = ({ children }) => (
  <span style={{ color: 'red' }}>{children}</span>
);

export const LocaleProvider = ({ locale, children }) => {
  // this may be redundant since there are propTypes defined
  if (!isLocaleSupported(locale)) {
    // eslint-disable-next-line
    console.error(`Locale "${locale}" is not available in supported locales.`);
  }

  return (
    <InternationalizationContext.Consumer>
      {/* eslint-disable-next-line */}
      {({ locale: inheritedLocale, messages }) => (
        <InternationalizationContext.Provider value={{ locale, messages }}>
          {children}
        </InternationalizationContext.Provider>
      )}
    </InternationalizationContext.Consumer>
  );
};

LocaleProvider.propTypes = {
  locale: propTypes.oneOf(availableLocales),
};

export const MessagesProvider = ({ messages, children }) => (
  <InternationalizationContext.Consumer>
    {/* eslint-disable-next-line */}
    {({ locale, messages: inheritedMessages }) => (
      <InternationalizationContext.Provider value={{ locale, messages }}>
        {children}
      </InternationalizationContext.Provider>
    )}
  </InternationalizationContext.Consumer>
);

MessagesProvider.propTypes = {
  messages: propTypes.objectOf(propTypes.objectOf(propTypes.node)),
};



// WEBPACK FOOTER //
// ./src/modules/Internationalization/index.js